import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "./pages/Login";
import NavigationBar from "./components/NavigationBar";
import "./App.css";
import PromotersMode from "./pages/PromotersMode";
import { routes } from "./routes/routes";
import AuthClient from "./api/AuthClient";
import { SelectedGroupProvider } from "./GlobalContext/groupDetailContext";
import LoggedNavigationBar from "./components/NavigationBar/LoggedNavBar";
import MakeOfferPages from "./components/MakeOfferPages";
import OfferDetailPage from "./pages/OfferDetailPage";
import CarouselView from "./components/PromoterGroupDetail";
import ProfilePhotoZoom from "./components/PromoterGroupDetail/ProfilePhotoZoom";
import { ClubsProvider } from "./GlobalContext/selectedClubsContext";
import GuestMode from "./pages/GuestMode";
import { useEffect, useState } from "react";
import MyPartyDetails from "./pages/MyPartyDetails";
import { loggedNavbarItems, navbarItems } from "./routes/navBarItems";
import AddMissingClub from "./components/MakeOfferPages/AddMissingClub";
import CitySelector from "./components/CitySelector/CitySelector";
import AlsoGoingOutDetailPage from "./components/GuestMode/AlsoGoingOutDetailPage";
import { PartyProvider } from "./GlobalContext/bookingTypeContext";
import { ListingProvider } from "./GlobalContext/requestedListings";
import { PartiesProvider } from "./GlobalContext/partiesContext";
import { AlsoGoingOutGroupProvider } from "./GlobalContext/selectedAlsoGoingOutGroupContext";
import { OpenClubsProvider } from "./GlobalContext/openClubsContext";
import UserProfile from "./components/UserProfile/UserProfile";
import EditProfile from "./components/UserProfile/EditProfile";
import AddGirlsOrBoys from "./components/AddFriends/AddGirlsOrBoys";
import FriendsPhotos from "./components/AddFriends/FriendsPhotos";
import FriendsAge from "./components/AddFriends/FriendsAge";
import AddAppFriends from "./components/AddFriends/AddAppFriends";
import { AllCitiesProvider } from "./AllCitiesContext";
import InvitedFriendInfo from "./components/MyPartyDetail/InvitedFriends/InvitedFriendInfo";
import RequestClub from "./components/MyPartyDetail/RequestClub";
import { SelectedImageFileProvider } from "./SelectedImageContext";
import { CameraProvider } from "./CameraContext";
import ChatPages from "./components/ChatPages";
import { LoadingAnimationProvider } from "./GlobalContext/loadingAnimation";
import { CurrentPathProvider } from "./GlobalContext/currentPathContext";
import { SignalRConnectionStateProvider } from "./GlobalContext/signalRContext";
import { UserProfileInfoProvider } from "./GlobalContext/UserProfileInfoContext";
import { MakeOfferProvider } from "./GlobalContext/makeOfferContext";
import AppMode from "./api/enums/AppMode";
import {
  appModeChangedEventName,
  ensureValidAppModeForUser,
  getStoredAppMode,
  isPromoterUserType,
} from "./utils/appMode";


const App = () => {
  const [authClient] = useState(() => new AuthClient());
  const [authResponse, setAuthResponse] = useState(() => authClient.getAuthenticationResponse());
  const token = authResponse?.token;
  const userTypeValue = `${authResponse?.userType ?? ""}`;
  const isPromoterUser = isPromoterUserType(userTypeValue);
  const [appMode, setAppMode] = useState<AppMode>(() => {
    if (!authResponse?.token) {
      return AppMode.GuestMode;
    }

    return ensureValidAppModeForUser(`${authResponse.userType ?? ""}`);
  });

  useEffect(() => {
    const refresh = () => setAuthResponse(authClient.getAuthenticationResponse());
    window.addEventListener('login', refresh);
    window.addEventListener('logout', refresh);

    return () => {
      window.removeEventListener("login", refresh);
      window.removeEventListener("logout", refresh);
    };
  }, [authClient]);

  useEffect(() => {
    const syncAppMode = () => {
      if (!token) {
        setAppMode(AppMode.GuestMode);
        return;
      }

      setAppMode(getStoredAppMode());
    };

    window.addEventListener(appModeChangedEventName, syncAppMode);

    return () => {
      window.removeEventListener(appModeChangedEventName, syncAppMode);
    };
  }, [token]);

  useEffect(() => {
    if (!token) {
      setAppMode(AppMode.GuestMode);
      return;
    }

    setAppMode(ensureValidAppModeForUser(userTypeValue));
  }, [token, userTypeValue]);

  // SSO: if not logged in via localStorage, check if the website set a ClubrAuth cookie
  useEffect(() => {
    if (authClient.isLoggedIn()) {
      setAuthResponse(authClient.getAuthenticationResponse());
      return;
    }

    authClient
      .tryRestoreSession()
      .then(data => {
        if (data?.token) {
          setAuthResponse(data);
        }
      })
      .catch(err => console.warn("SSO session check failed:", err));
  }, [authClient]);

  const handleLogout = () => {
    void authClient.logout().then(() => {
      window.location.href = "/rapp";
    });
  };

  return (
    <SelectedGroupProvider>
      <OpenClubsProvider>
        <ClubsProvider>
          <MakeOfferProvider>

          <PartyProvider>
            <ListingProvider>

              <UserProfileInfoProvider>
                <AllCitiesProvider>
                  <PartiesProvider>
                    <AlsoGoingOutGroupProvider>

                      <SelectedImageFileProvider>
                        <CameraProvider>
                          <LoadingAnimationProvider>

                            <CurrentPathProvider>
                              <SignalRConnectionStateProvider>

                                <BrowserRouter basename={"/rapp"}>
                                  <div className="App">
                                    {token && token !== undefined ? (
                                      <LoggedNavigationBar
                                        navbarItems={loggedNavbarItems}
                                        handleLogout={handleLogout}
                                      />
                                    ) : (
                                      <NavigationBar navbarItems={navbarItems} />
                                    )}
                                    <Routes>
                                      <Route
                                        path={routes.Root}
                                        element={
                                          isPromoterUser
                                            ? (
                                              appMode === AppMode.PromoterMode
                                                ? <Navigate to={routes.PromotersMode} replace />
                                                : <Navigate to={routes.GuestMode} replace />
                                            )
                                            : <GuestMode />
                                        }
                                      />
                                      <Route path={routes.MyParties} element={<GuestMode />} />
                                      <Route path={routes.Login} element={<Login />} />
                                      <Route path={routes.PromotersMode} element={<PromotersMode />} />
                                      <Route path={routes.GuestMode} element={<GuestMode />} />

                                      <Route path={routes.MyPartyWithId(":id")} element={<MyPartyDetails />} />
                                      <Route path={routes.RequestClub(":id")} element={<RequestClub />} />
                                      <Route path={routes.PromGroupDetailPage(":city", ":groupId")} element={<CarouselView />} />
                                      <Route path={routes.AlsoGoingOutDetailPage(":groupId")} element={<AlsoGoingOutDetailPage />} />

                                      <Route path={routes.PhotoZoom(":callBackUrl", ":city", ":Id", ":photoId")} element={<ProfilePhotoZoom />} />
                                      <Route path={routes.MakeOffer(":city", ":groupId")} element={<MakeOfferPages />} />
                                      <Route path={routes.OfferDetails(":partyId", ":offerId")} element={<OfferDetailPage />} />

                                      <Route path={routes.MissingClub} element={< AddMissingClub />} />
                                      <Route path={routes.CitySelector} element={<CitySelector />} />
                                      <Route path={routes.UserProfile} element={<UserProfile />} />
                                      <Route path={routes.EditProfile} element={<EditProfile />} />

                                      <Route path="/addMalesOrFemales" element={<AddGirlsOrBoys />} />
                                      <Route path="/friendsPhotos" element={<FriendsPhotos />} />
                                      <Route path="/friendsAge" element={<FriendsAge />} />
                                      <Route path="/appFriends" element={<AddAppFriends />} />

                                      <Route path={routes.InvitedFriendInfo(":groupId", ":friendId")} element={<InvitedFriendInfo />} />
                                      <Route path={routes.ChatPages} element={<ChatPages />} />
                                    </Routes>
                                  </div>
                                </BrowserRouter>

                              </SignalRConnectionStateProvider>
                            </CurrentPathProvider>

                          </LoadingAnimationProvider>
                        </CameraProvider>
                      </SelectedImageFileProvider>

                    </AlsoGoingOutGroupProvider>
                  </PartiesProvider>
                </AllCitiesProvider>
              </UserProfileInfoProvider>

            </ListingProvider>
          </PartyProvider>

          </MakeOfferProvider>
        </ClubsProvider>
      </OpenClubsProvider>
    </SelectedGroupProvider >
  );
};

export default App;
